// The terrain read as FEATURES: named bodies of rock and water (R548).
//
// The terrain layers draw the ground as a field. The chart draws it as a
// list of THINGS: this ridge, that trench, the rim of one of them, the
// channel between two of them, the depth contours inside one of them. Card
// 13 (isobaths), 14 (bottom nature), 18 (coastal profile), 19 (place names)
// and 26 (hazards) all ask the same question first — "which bodies are
// there, and where does each one begin and end" — so one leaf answers it.
//
// **ONE OWNER FOR THE COMPONENT WALK.** `labelComponents` below is the walk
// `fast/terrain-prep.ts` used to hold privately. The prep still calls it, so
// the shader's `compR` and the chart's feature list can never disagree about
// what one body is.
//
// **THE PLANE'S DISTANCE IS AN INSIDE DISTANCE.** `terrain-prep.ts` writes
// R (trench) and G (ridge) as the world-px distance from a point INSIDE a
// body to its rim: 0 everywhere outside the body, growing to the body's
// inradius at its deepest point. `isobaths` therefore draws a contour that
// runs a stated depth INSIDE the rim. The plane carries no outside distance
// at all, so an offset OUTSIDE a body cannot be asked of this leaf.

import { BLOCK_RIDGE, BLOCK_TRENCH, HEX_SIZE, cellAt, cellCenter, neighborAt } from '../world';
import type { Loop, Point } from './contours';
import { unionOutline } from './hex-ring';

/** The two kinds of body a map holds. */
export type FeatureKind = 'ridge' | 'trench';

/** An axis-aligned box in world px. */
export interface FeatureBox {
  x0: number; y0: number; x1: number; y1: number;
}

/** One connected body of rock or of trench floor. */
export interface Feature {
  /**
   * Unique across the ridges AND the trenches of one `terrainFeatures`
   * call, so the list can go straight to `assignFeatureNames`.
   */
  id: number;
  kind: FeatureKind;
  /** Every cell of the body, in ascending cell index. */
  cells: number[];
  /** `cells.length`, which is the figure card 18 prints. */
  size: number;
  /** The mean of the cells' centres, in world px. */
  centroid: { x: number; y: number };
  /** The box that holds every hex of the body, in world px. */
  bbox: FeatureBox;
  /** The hex-bound outline: the outer loops first, then any hole. */
  outline: Loop[];
  /** True for a ridge of exactly one cell — the chart's `Rk`, card 26. */
  singleRock: boolean;
  /** The cells with at least one water neighbour, in ascending index. */
  rimCells: number[];
}

/**
 * What `isobaths` needs of the terrain plane. `TerrainPrep` from
 * `fast/terrain-prep.ts` satisfies it; the shape is restated here rather
 * than imported, so this leaf carries no path into the engine.
 */
export interface TerrainPlane {
  /** RGBA interleaved, `distW * distH * 4` floats. */
  plane: Float32Array;
  distW: number;
  distH: number;
  /** The world point of texel (0,0)'s low corner. */
  loX: number;
  loY: number;
  /** The raster's texel pitch in world px. */
  texelPx: number;
  /** One float per cell: the inradius of the cell's body, in world px. */
  compR: Float32Array;
}

/** One narrow gap of water between two bodies of rock. */
export interface Channel {
  /** The water cells of the gap, in ascending cell index. */
  cells: number[];
  /** 1 or 2 — how many water cells stand between the two rock cells. */
  width: number;
  /** The rock cell on one side. Always the lower of the two indices. */
  a: number;
  /** The rock cell on the other side. */
  b: number;
}

/** How many floats one texel of the plane carries. */
const PLANE_CHANNELS = 4;

/** Half a hex's width, which is what grows a centre into a box. */
const HALF_W = (Math.sqrt(3) / 2) * HEX_SIZE;

/**
 * The connected components of one terrain kind, by hex walk.
 *
 * `label[i]` is the component of cell `i`, or −1 when the cell is of another
 * kind. `sizes[c]` is the cell count of component `c`, and `sizes.length` is
 * the component count. Components are numbered in ascending seed-cell order,
 * so the labelling is a function of the mask alone.
 */
export function labelComponents(
  blocked: Uint8Array, kind: number, cols: number, rows: number,
): { label: Int32Array; sizes: number[] } {
  const cells = cols * rows;
  const label = new Int32Array(cells).fill(-1);
  const queue = new Int32Array(cells);
  const sizes: number[] = [];
  for (let seed = 0; seed < cells; seed++) {
    if (blocked[seed] !== kind || label[seed] >= 0) continue;
    const c = sizes.length;
    let size = 0;
    let head = 0, tail = 0;
    label[seed] = c;
    queue[tail++] = seed;
    while (head < tail) {
      const i = queue[head++];
      size++;
      for (let k = 0; k < 6; k++) {
        const n = neighborAt(i, k, cols, rows);
        if (n >= 0 && blocked[n] === kind && label[n] < 0) { label[n] = c; queue[tail++] = n; }
      }
    }
    sizes.push(size);
  }
  return { label, sizes };
}

/** The features of one kind, numbered from `firstId`. */
function featuresOf(
  blocked: Uint8Array, kind: FeatureKind, mask: number,
  cols: number, rows: number, firstId: number,
): Feature[] {
  const { label, sizes } = labelComponents(blocked, mask, cols, rows);
  const cellsOf: number[][] = sizes.map(() => []);
  for (let i = 0; i < label.length; i++) {
    if (label[i] >= 0) cellsOf[label[i]].push(i);
  }
  return cellsOf.map((cells, c) => {
    let sx = 0, sy = 0;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const cell of cells) {
      const [cx, cy] = cellCenter(cell, cols);
      sx += cx; sy += cy;
      if (cx - HALF_W < x0) x0 = cx - HALF_W;
      if (cy - HEX_SIZE < y0) y0 = cy - HEX_SIZE;
      if (cx + HALF_W > x1) x1 = cx + HALF_W;
      if (cy + HEX_SIZE > y1) y1 = cy + HEX_SIZE;
    }
    const size = cells.length;
    return {
      id: firstId + c,
      kind,
      cells,
      size,
      centroid: { x: sx / size, y: sy / size },
      bbox: { x0, y0, x1, y1 },
      outline: unionOutline(cells, cols, rows),
      singleRock: kind === 'ridge' && size === 1,
      rimCells: rimCellsOf(cells, blocked, cols, rows),
    };
  });
}

/**
 * The cells of a body that touch water, in ascending cell index.
 *
 * The water outside the board counts, exactly as the terrain raster reads
 * it, so a ridge on the map edge still has a rim. Rock beside a trench is
 * NOT rim: the two kinds are separate bodies, and a mark meant for open
 * water must not stand on a trench floor.
 */
function rimCellsOf(
  cells: readonly number[], blocked: Uint8Array, cols: number, rows: number,
): number[] {
  const out: number[] = [];
  for (const cell of cells) {
    for (let k = 0; k < 6; k++) {
      const nb = neighborAt(cell, k, cols, rows);
      if (nb < 0 || blocked[nb] === 0) { out.push(cell); break; }
    }
  }
  return out;
}

/**
 * Every ridge and every trench of a map, as features.
 *
 * The ids run from 0 over the ridges and continue over the trenches, so no
 * two features of one call share an id.
 */
export function terrainFeatures(
  blocked: Uint8Array, cols: number, rows: number,
): { ridges: Feature[]; trenches: Feature[] } {
  const ridges = featuresOf(blocked, 'ridge', BLOCK_RIDGE, cols, rows, 0);
  const trenches = featuresOf(blocked, 'trench', BLOCK_TRENCH, cols, rows, ridges.length);
  return { ridges, trenches };
}

// ---------------------------------------------------------------------------
// Isobaths
// ---------------------------------------------------------------------------

/** The plane channel of a kind: R is the trench, G is the ridge. */
function channelOf(kind: FeatureKind): number {
  return kind === 'trench' ? 0 : 1;
}

/** A canonical id for the horizontal edge from texel (i,j) to (i+1,j). */
function edgeH(i: number, j: number, w: number): number { return (j * w + i) * 2; }

/** A canonical id for the vertical edge from texel (i,j) to (i,j+1). */
function edgeV(i: number, j: number, w: number): number { return (j * w + i) * 2 + 1; }

/** A texel rectangle the tracer scans: `[i0, i1)` by `[j0, j1)`. */
export interface TexelRect { i0: number; i1: number; j0: number; j1: number }

/**
 * The iso-contours of one plane channel, at one level.
 *
 * Marching squares over the texel lattice. Every crossing point is computed
 * ONCE, keyed by the lattice edge it sits on, so the two cells that share an
 * edge take the identical point and the chained loop has no hairline gap.
 * A chord is oriented with the deep side on its left, which is the winding
 * `traceHexLoops` gives an outer loop.
 */
function contourAt(p: TerrainPlane, ch: number, level: number, rect?: TexelRect): Loop[] {
  const { plane, distW: w, distH: h, loX, loY, texelPx } = p;
  // The scan is bounded to the rectangle a caller names, because one full
  // scan of the plane costs about a million texel reads and a piece that
  // asked for one per body and per offset stalled a frame for seconds
  // (R548). A body's contours lie inside the body, so its box bounds them.
  const i0 = Math.max(0, rect?.i0 ?? 0), j0 = Math.max(0, rect?.j0 ?? 0);
  const i1 = Math.min(w, rect?.i1 ?? w), j1 = Math.min(h, rect?.j1 ?? h);
  const val = (i: number, j: number) => plane[(j * w + i) * PLANE_CHANNELS + ch];
  const X = (i: number) => loX + texelPx * (i + 0.5);
  const Y = (j: number) => loY + texelPx * (j + 0.5);
  return traceField(val, w, texelPx, X, Y, level, i0, j0, i1, j1);
}

/**
 * Marching squares over one scalar field, read through `val`, inside the
 * texel rectangle `[i0, i1) × [j0, j1)`. `w` keys the edge ids, and `X`/`Y`
 * place a texel's centre in the world. The plane's channels and the outer
 * isobaths' own distance raster both come through here (R557).
 */
function traceField(
  val: (i: number, j: number) => number, w: number, texelPx: number,
  X: (i: number) => number, Y: (j: number) => number, level: number,
  i0: number, j0: number, i1: number, j1: number,
): Loop[] {
  const points = new Map<number, Point>();
  const pointOf = (id: number): Point => {
    let pt = points.get(id);
    if (pt) return pt;
    const cell = id >> 1;
    const i = cell % w, j = (cell / w) | 0;
    // An even id is a horizontal edge and an odd one is vertical.
    const across = (id & 1) === 0;
    const v0 = val(i, j);
    const v1 = across ? val(i + 1, j) : val(i, j + 1);
    const t = v1 === v0 ? 0.5 : (level - v0) / (v1 - v0);
    pt = across ? [X(i) + t * texelPx, Y(j)] : [X(i), Y(j) + t * texelPx];
    points.set(id, pt);
    return pt;
  };

  // Directed chords, keyed by the edge they leave.
  const outOf = new Map<number, number[]>();
  const emit = (from: number, to: number) => {
    const list = outOf.get(from);
    if (list) list.push(to);
    else outOf.set(from, [to]);
  };

  const hi = [false, false, false, false];
  const vals = [0, 0, 0, 0];

  for (let j = j0; j + 1 < j1; j++) {
    for (let i = i0; i + 1 < i1; i++) {
      vals[0] = val(i, j); vals[1] = val(i + 1, j);
      vals[2] = val(i + 1, j + 1); vals[3] = val(i, j + 1);
      let count = 0;
      for (let k = 0; k < 4; k++) { hi[k] = vals[k] > level; if (hi[k]) count++; }
      if (count === 0 || count === 4) continue;

      // Edge e runs between corner e and corner (e+1)%4, and the corners in
      // that order wind the cell POSITIVELY (the shoelace sign `loopArea`
      // reads). A chord from edge eA to edge eB therefore holds the corners
      // eB+1 … eA on its LEFT. Every case below picks eA and eB so the deep
      // corners fall on that side, which is the winding `traceHexLoops`
      // gives an outer loop. The rule is TOPOLOGY and not geometry on
      // purpose: a level that lands exactly on a texel value puts a chord's
      // two ends on one point, and a cross-product test would then orient
      // half the chords backwards and cut the contour into fragments.
      const edgeId = [edgeH(i, j, w), edgeV(i + 1, j, w), edgeH(i, j + 1, w), edgeV(i, j, w)];
      const chord = (eA: number, eB: number) => emit(edgeId[eA], edgeId[eB]);

      if (count === 2 && hi[0] === hi[2]) {
        // A saddle: the deep corners sit on a diagonal and all four edges
        // carry a crossing. The average of the four corners says which side
        // joins through the middle; the other side is then two lone
        // corners, and each one takes the chord that cuts it off.
        const avg = (vals[0] + vals[1] + vals[2] + vals[3]) / 4;
        for (let k = 0; k < 4; k++) {
          if (avg > level) {
            // The deep side joins: cut off each lone SHALLOW corner, which
            // leaves the other three corners on the chord's left.
            if (!hi[k]) chord((k + 3) % 4, k);
          } else if (hi[k]) {
            // The shallow side joins: cut off each lone DEEP corner.
            chord(k, (k + 3) % 4);
          }
        }
      } else {
        // One chord. The deep corners are a contiguous run of `count` of
        // them; find where the run starts and lay the chord round it.
        let start = 0;
        for (let k = 0; k < 4; k++) if (hi[k] && !hi[(k + 3) % 4]) { start = k; break; }
        chord((start + count - 1) % 4, (start + 3) % 4);
      }
    }
  }

  return chainChords(outOf, pointOf);
}

/**
 * Chains the directed chords into loops, following the edge ids.
 *
 * An open chain starts at its head, the edge that no chord enters, so it
 * comes out whole. A walk that started at a chord in the middle of an open
 * chain gave its back half as one piece and its front half as another, and
 * a reader that closed the front half drew a straight line across the body
 * (R2368). The heads go first, and the closed loops follow in scan order.
 */
function chainChords(
  outOf: Map<number, number[]>, pointOf: (id: number) => Point,
): Loop[] {
  const loops: Loop[] = [];
  const entered = new Set<number>();
  for (const list of outOf.values()) for (const to of list) entered.add(to);
  const keys = [...outOf.keys()];
  const starts = [
    ...keys.filter((id) => !entered.has(id)),
    ...keys.filter((id) => entered.has(id)),
  ];
  for (const start of starts) {
    let list = outOf.get(start);
    while (list && list.length > 0) {
      const loop: Loop = [pointOf(start)];
      let at = list.pop()!;
      if (list.length === 0) outOf.delete(start);
      let guard = 0;
      while (at !== start) {
        loop.push(pointOf(at));
        const next = outOf.get(at);
        if (!next || next.length === 0) break; // open chain; keep what it drew
        const step = next.pop()!;
        if (next.length === 0) outOf.delete(at);
        at = step;
        if (++guard > 1e6) break;
      }
      if (loop.length >= 3) loops.push(loop);
      list = outOf.get(start);
    }
  }
  return loops;
}

/**
 * The iso-contours of one terrain kind, one set of loops per offset.
 *
 * `offsetsPx` are DEPTHS INSIDE the body, in world px: 0 hugs the rim, 14
 * runs 14 px in from it. An offset under 0 draws nothing, because the plane
 * carries no distance outside a body (see the note at the head of the file).
 */
export function isobaths(
  prep: TerrainPlane, kind: FeatureKind, offsetsPx: readonly number[],
): Loop[][] {
  const ch = channelOf(kind);
  return offsetsPx.map((offset) => (offset < 0 ? [] : contourAt(prep, ch, offset)));
}

/**
 * The texel rectangle that holds one body's contours: its world box, taken
 * to texel indices and padded by two texels, so a chord that crosses the
 * box edge is still traced. It is exported for the test.
 */
export function texelRectOf(p: TerrainPlane, box: FeatureBox): TexelRect {
  const pad = 2;
  const t = p.texelPx;
  return {
    i0: Math.max(0, Math.floor((box.x0 - p.loX) / t) - pad),
    i1: Math.min(p.distW, Math.ceil((box.x1 - p.loX) / t) + pad + 1),
    j0: Math.max(0, Math.floor((box.y0 - p.loY) / t) - pad),
    j1: Math.min(p.distH, Math.ceil((box.y1 - p.loY) / t) + pad + 1),
  };
}

/**
 * The iso-contours of ONE body, one set of loops per offset.
 *
 * An offset deeper than the body's own inradius (`compR`) draws nothing:
 * such a contour has no line to run along, and card 13 states the skip. The
 * loops of another body are dropped by the cell each loop stands on, so a
 * caller gets only the contours of the feature it asked about.
 */
export function isobathsOf(
  prep: TerrainPlane, feature: Feature, offsetsPx: readonly number[],
  cols: number, rows: number,
): Loop[][] {
  const ch = channelOf(feature.kind);
  const inradius = feature.cells.length > 0
    ? Math.abs(prep.compR[feature.cells[0]] ?? 0) : 0;
  const mine = new Set(feature.cells);
  const rect = texelRectOf(prep, feature.bbox);
  return offsetsPx.map((offset) => {
    if (offset < 0 || offset > inradius) return [];
    return contourAt(prep, ch, offset, rect).filter((loop) => {
      // A contour runs inside its body, so the cell under any of its points
      // belongs to that body. Two points are read, because a loop at offset
      // 0 can put a point half a texel outside the rim.
      for (const [x, y] of [loop[0], loop[(loop.length / 2) | 0]]) {
        const cell = cellAt(x, y, cols, rows);
        if (cell >= 0 && mine.has(cell)) return true;
      }
      return false;
    });
  });
}

/**
 * The contours OUTSIDE one body, one set of loops per offset (R557).
 *
 * The plane carries no distance outside a body, so the piece cannot read
 * these off it. The rim is traced first, at `rimPx` inside the body, and a
 * distance raster is then laid over the body's box padded by the deepest
 * offset: every texel outside the body's own cells holds its distance to
 * the nearest rim point, and every texel on the body holds 0. The rings are
 * the contours of that raster at each offset. A contour of a distance field
 * never crosses itself, so a bay in the rim gives a rounded ring and not a
 * fold.
 *
 * The raster's pitch is `fieldTexels` plane texels, because the field is
 * smooth and the piece thins each loop past the plane's own pitch anyway.
 * The cost is the raster's texels times the rim's points, once per plane.
 */
export function outerIsobathsOf(
  prep: TerrainPlane, feature: Feature, offsetsPx: readonly number[],
  cols: number, rows: number, rimPx: number, fieldTexels: number,
): Loop[][] {
  const deepest = Math.max(0, ...offsetsPx);
  if (deepest <= 0 || feature.cells.length === 0) return offsetsPx.map(() => []);
  const rim = isobathsOf(prep, feature, [rimPx], cols, rows)[0];
  const pitch = prep.texelPx * Math.max(1, fieldTexels);
  const rimPts: Point[] = [];
  for (const loop of rim) {
    let last: Point | null = null;
    for (const pt of loop) {
      if (last && Math.hypot(pt[0] - last[0], pt[1] - last[1]) < pitch) continue;
      rimPts.push(pt);
      last = pt;
    }
  }
  if (rimPts.length === 0) return offsetsPx.map(() => []);

  // The raster: the body's box padded by the deepest ring and two texels.
  // It is NOT clipped to the plane: the field reads the rim points alone,
  // so it is good past the plane, and every ring comes back closed. The
  // piece cuts a ring at the map edge (R2211, R2368).
  const pad = deepest + 2 * pitch;
  const box = feature.bbox;
  const x0 = box.x0 - pad, y0 = box.y0 - pad;
  const x1 = box.x1 + pad, y1 = box.y1 + pad;
  const w = Math.max(2, Math.ceil((x1 - x0) / pitch) + 1);
  const h = Math.max(2, Math.ceil((y1 - y0) / pitch) + 1);
  const X = (i: number) => x0 + pitch * (i + 0.5);
  const Y = (j: number) => y0 + pitch * (j + 0.5);
  const mine = new Set(feature.cells);
  const field = new Float32Array(w * h);
  for (let j = 0; j < h; j++) {
    const y = Y(j);
    for (let i = 0; i < w; i++) {
      const x = X(i);
      const cell = cellAt(x, y, cols, rows);
      if (cell >= 0 && mine.has(cell)) continue; // 0 on the body itself
      let best = Infinity;
      for (const [px, py] of rimPts) {
        const d = (px - x) * (px - x) + (py - y) * (py - y);
        if (d < best) best = d;
      }
      field[j * w + i] = Math.sqrt(best);
    }
  }
  const val = (i: number, j: number) => field[j * w + i];
  return offsetsPx.map((offset) => (
    offset <= 0 ? [] : traceField(val, w, pitch, X, Y, offset, 0, 0, w, h)
  ));
}

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

/**
 * Every narrow gap of water between two SEPARATE bodies of rock.
 *
 * A gap is 1 or 2 water cells wide, measured along one of the three hex
 * axes, and the rock on either side must belong to two different components:
 * a notch in one ridge is not a channel, because nothing passes through it
 * to anywhere. A mission line may run through a channel, and card 50 marks
 * it, so the answer is a list and not a picture.
 *
 * The list is in ascending order of the gap's first cell. A cell can stand
 * in more than one channel, because two channels can cross.
 */
export function channels(
  blocked: Uint8Array, cols: number, rows: number,
): Channel[] {
  const { label } = labelComponents(blocked, BLOCK_RIDGE, cols, rows);
  const isRock = (i: number) => i >= 0 && blocked[i] === BLOCK_RIDGE;
  const out: Channel[] = [];
  const push = (cells: number[], width: number, r1: number, r2: number) => {
    if (label[r1] === label[r2]) return;
    out.push({ cells, width, a: Math.min(r1, r2), b: Math.max(r1, r2) });
  };
  const cells = cols * rows;
  for (let i = 0; i < cells; i++) {
    if (blocked[i] !== 0) continue;
    // Width 1: rock on both sides of one water cell, along one axis.
    for (let k = 0; k < 3; k++) {
      const r1 = neighborAt(i, k, cols, rows);
      const r2 = neighborAt(i, k + 3, cols, rows);
      if (isRock(r1) && isRock(r2)) push([i], 1, r1, r2);
    }
    // Width 2: two water cells in a row, rock beyond each end. The second
    // cell must be the higher index, so a pair is found once.
    for (let k = 0; k < 6; k++) {
      const j = neighborAt(i, k, cols, rows);
      if (j <= i || blocked[j] !== 0) continue;
      const r1 = neighborAt(i, (k + 3) % 6, cols, rows);
      const r2 = neighborAt(j, k, cols, rows);
      if (isRock(r1) && isRock(r2)) push([i, j], 2, r1, r2);
    }
  }
  return out;
}
